import {
  initPlasmicLoader,
  ComponentRenderData,
} from '@plasmicapp/loader-react';
import createPersistedState from '~/react-hooks/use-persisted-state';
import axios, { AxiosError } from 'axios';
import { DefaultGetResponse } from './rest';
import { UseQueryOptions, useQuery } from 'react-query';
import { PlasmicModelType, UpgradeModal } from '~/types/models/plasmic';
import { UpgradeModalTrigger } from '../Components/Modals/UpgradeModalFlow/util';

export const queryKeys = {
  all: ['plasmic-cms'] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (model: PlasmicModelType, keys?: Record<string, string>) =>
    [...queryKeys.details(), model, keys] as const,
};

export const PLASMIC = initPlasmicLoader({
  projects: [
    {
      id: process.env.PLASMIC_PROJECT_ID!,
      token: process.env.PLASMIC_API_TOKEN!,
    },
  ],
  // Fetches the latest revisions, whether or not they were unpublished!
  // Disable for production to ensure you render only published changes.
  preview: process.env.NODE_ENV !== 'production',
});

export const usePlasmicResults = createPersistedState(
  'plasmic-all-pages-results'
);

export type PersistentPlasmicResults = {
  timestamp: number;
  results: Array<
    | (ComponentRenderData & {
        slug: string;
      })
    | null
  >;
};

//TODO: change the slugs once the plasmic pages are ready. https://bonjoro.atlassian.net/browse/PLG-532
export enum PlasmicSlugs {
  ScreenRecorder = '/screen-recorder',
  VideoMessagingPaywallPage = '/vm-trial-info-page',
  TestimonialPaywallPage = '/tm-trial-info-page',
  FallbackLockedPage = '/vm-fallback-locked-page',
  NoAccessToService = '/no-access-yet',
}

export const initPlasmicPages = async () => {
  const [plasmicResults, setPlasmicResults] =
    usePlasmicResults<PersistentPlasmicResults>();

  const pages = (await Promise.all(
    Object.values(PlasmicSlugs).map(async (slug) => {
      const plasmicData = await PLASMIC.maybeFetchComponentData(slug);
      if (plasmicData) {
        plasmicData['slug'] = slug;
      }

      return plasmicData;
    })
  )) as PersistentPlasmicResults['results'];

  const currentTimestamp = Date.now();
  const EXP_IN_MINUTES = 60;

  if (
    !plasmicResults ||
    Math.round((currentTimestamp - plasmicResults.timestamp) / 60000) >
      EXP_IN_MINUTES
  ) {
    setPlasmicResults({
      timestamp: Date.now(),
      results: pages,
    });
  }
};

// The Plasmic CMS Row structure
export interface PlasmicCMSRow<PlasmicModel> {
  id: string;
  createdAt: string;
  updatedAt: string;
  data: PlasmicModel;
  identifier: string;
}

// The actual Plasmic CMS Response structure
// Ref: https://docs.plasmic.app/learn/plasmic-cms-api-reference/#example-load-all-items-of-a-model
export interface GetPlasmicCMSResponse<PlasmicModel> {
  rows: PlasmicCMSRow<PlasmicModel>[];
}

export interface GetPlasmicCMSParams {
  model: PlasmicModelType;
  keys?: Record<string, string>;
  query?: Record<string, string | string[] | undefined>;
  limit?: number;
}

export async function getPlasmicCMS<PlasmicModel>({
  model,
  query,
  limit,
}: GetPlasmicCMSParams) {
  const cmsId = process.env.PLASMIC_CMS_ID!;
  const apiToken = process.env.PLASMIC_CMS_API_TOKEN!;

  const apiUrl = new URL(
    `https://data.plasmic.app/api/v1/cms/databases/${cmsId}/tables/${model}/query`
  );

  if (query || limit) {
    apiUrl.search = new URLSearchParams({
      q: JSON.stringify({
        where: query,
        limit,
      }),
    }).toString();
  }

  const res = await axios.get<GetPlasmicCMSResponse<PlasmicModel>>(
    apiUrl.toString(),
    {
      headers: { 'x-plasmic-api-cms-tokens': `${cmsId}:${apiToken}` },
    }
  );

  return res.data.rows.map((row) => {
    return row.data;
  });
}

export interface UsePlasmicCMSHookArgs<PlasmicModel>
  extends GetPlasmicCMSParams {
  options?: UseQueryOptions<DefaultGetResponse<PlasmicModel[]>, AxiosError>;
}

export function usePlasmicCMS<PlasmicModel>({
  model,
  keys,
  query,
  limit,
  options,
}: UsePlasmicCMSHookArgs<PlasmicModel>) {
  return useQuery<DefaultGetResponse<PlasmicModel[]>, AxiosError>(
    queryKeys.detail(model, keys),
    () => getPlasmicCMS<PlasmicModel>({ model, query, limit }),
    options
  );
}

export interface UsePlasmicUpgradeModalArgs {
  uid?: UpgradeModalTrigger;
  options?: UseQueryOptions<DefaultGetResponse<UpgradeModal[]>, AxiosError>;
}

export function usePlasmicUpgradeModal(props?: UsePlasmicUpgradeModalArgs) {
  const useQueryResult = usePlasmicCMS<UpgradeModal>({
    model: PlasmicModelType.UpgradeModal,
    keys: { uid: props?.uid } as Record<string, string>,
    options: {
      ...props?.options,
      enabled: props?.uid !== undefined,
    },
    query: {
      uid: props?.uid,
    },
  });

  return {
    ...useQueryResult,
    data: useQueryResult.data?.find((modal) => modal.uid === props?.uid),
  };
}
